import type { DatapointsQueryProperties } from '@cognite/sdk';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';

import { useCogniteClient } from '#core/hooks/useCogniteClient.ts';
import {
  type DatapointsBatchItem,
  datapointsResultKey,
  fetchDatapointsBatched,
  toRetrieveItem,
} from '#core/lib/timeseries/fetch-datapoints-batched.ts';

import {
  computePerSeriesLimit,
  type PendingRequest,
  TS_REF_DEFAULT_WINDOW_DAYS,
} from '#plugin-chat/lib/source-fetchers/helpers.ts';

type BatchState = {
  readonly defaultStart: number;
  readonly defaultEnd: number;
  readonly enqueue: (req: PendingRequest) => void;
};

const BATCH_STATES = new WeakMap<QueryClient, BatchState>();

export function useBatchState(): BatchState {
  const client = useCogniteClient();
  const queryClient = useQueryClient();
  const existing = BATCH_STATES.get(queryClient);

  // Pinned once per QueryClient (not per factory call) so every flush
  // for this client produces a stable TanStack cache key — a fresh
  // `Date.now()` per render would otherwise drift the default window
  // and defeat cross-flush dedup.
  const defaultEnd = Date.now();
  const defaultStart = defaultEnd - TS_REF_DEFAULT_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  // imperative (CLAUDE.md side-effect carve-out, pre-approved): a mutable
  // microtask-batching queue. `enqueue` pushes pending requests; a single
  // queueMicrotask `flush` drains them via `splice` and settles each request's
  // promise (`resolve`/`reject`) after the coalesced CDF call. Promise
  // settlement is the genuine side effect a pure transform cannot express.
  const pending: PendingRequest[] = [];

  const flush = async (): Promise<void> => {
    const batch = pending.splice(0);
    if (batch.length === 0) {
      return;
    }
    // One CDF call per distinct fetch spec (window + granularity +
    // aggregates). Within a group, items are deduped by composite
    // `(space, externalId)` identity (so two refs sharing an externalId
    // across spaces stay distinct) and chunked by
    // `fetchDatapointsBatched`. Each item uses the `instanceId` query
    // form when `space` is known; bare `externalId` otherwise.
    const groups = batch.reduce((acc, req) => {
      const g = acc.get(req.specKey);
      if (g === undefined) {
        acc.set(req.specKey, { spec: req.spec, reqs: [req] });
      } else {
        g.reqs.push(req);
      }
      return acc;
    }, new Map<string, { spec: DatapointsQueryProperties; reqs: PendingRequest[] }>());
    await Promise.all(
      Array.from(groups.values()).map(async ({ spec, reqs }) => {
        const dedupedItems = Array.from(
          reqs
            .reduce((acc, { space, externalId }) => {
              const key = datapointsResultKey({ space, externalId });
              if (!acc.has(key)) {
                acc.set(key, toRetrieveItem({ space, externalId }));
              }
              return acc;
            }, new Map<string, DatapointsBatchItem>())
            .values(),
        );
        const limit = computePerSeriesLimit(dedupedItems.length);
        try {
          const result = await fetchDatapointsBatched({
            client,
            queryClient,
            items: dedupedItems,
            spec: { ...spec, limit },
          });
          reqs.forEach((request) => {
            request.resolve(result.get(datapointsResultKey({ space: request.space, externalId: request.externalId })));
          });
        } catch (e) {
          const error = e;
          reqs.forEach((request) => {
            request.reject(error);
          });
        }
      }),
    );
  };

  const enqueue = (req: PendingRequest): void => {
    pending.push(req);
    // `enqueue` runs synchronously inside render (suspense queryFns), so it
    // must stay render-inert — no React state. The queue length doubles as
    // the "flush scheduled" flag: `flush` drains via `splice`, so length 1
    // means this push is the first since the last drain.
    if (pending.length === 1) {
      queueMicrotask(flush);
    }
  };

  const state: BatchState = { defaultStart, defaultEnd, enqueue };

  if (existing !== undefined) {
    return existing;
  }

  BATCH_STATES.set(queryClient, state);
  return state;
}
